import { EnvironmentProviders, inject, makeEnvironmentProviders, Provider, Signal, Type } from '@angular/core';
import {
  DEFAULT_SHARED_UI_ASSETS_BASE_URL,
  DEFAULT_SHARED_UI_LANGUAGE,
  SHARED_UI_CONFIG,
  SHARED_UI_PROFILE_COUNTRY,
  SharedUiConfig,
  SOFF_API_CONFIG,
} from './shared-ui.config';
import { SoffNotifier } from '../notifications/soff-notifier';

export type SharedUiFeatureKind = 'notifier' | 'savedViews' | 'profileCountry';

export interface SharedUiFeature {
  readonly kind: SharedUiFeatureKind;
  readonly providers: Provider[];
}

/**
 * Registers the shared-ui configuration (`SHARED_UI_CONFIG`, `SOFF_API_CONFIG`) and optional features.
 * The factory overload runs in an injection context, so `inject(...)` works there.
 */
export function provideSharedUi(config: SharedUiConfig | (() => SharedUiConfig), ...features: SharedUiFeature[]): EnvironmentProviders {
  const resolve = (value: SharedUiConfig): Required<SharedUiConfig> => ({
    defaultLanguage: DEFAULT_SHARED_UI_LANGUAGE,
    assetsBaseUrl: DEFAULT_SHARED_UI_ASSETS_BASE_URL,
    ...value,
  });

  return makeEnvironmentProviders([
    { provide: SHARED_UI_CONFIG, useFactory: () => resolve(typeof config === 'function' ? config() : config) },
    { provide: SOFF_API_CONFIG, useFactory: () => inject(SHARED_UI_CONFIG).api },
    ...features.flatMap(feature => feature.providers),
  ]);
}

/** Override the default snackbar notifier (used by saved views and the ticket UI). */
export function withNotifier(impl: Type<SoffNotifier>): SharedUiFeature {
  return { kind: 'notifier', providers: [{ provide: SoffNotifier, useClass: impl }] };
}

/** Fallback for the user's country when none was picked explicitly (the profile's country isn't always accurate). The factory runs in an injection context. */
export function withProfileCountry(country: () => Signal<string | null | undefined>): SharedUiFeature {
  return { kind: 'profileCountry', providers: [{ provide: SHARED_UI_PROFILE_COUNTRY, useFactory: country }] };
}
